/*
  Queen's cart page behaviour, ported for Dawn
  ============================================

  Copied verbatim from copyofqueen1-theme/assets/petplus-cart.js - no changes
  were needed. It was already vanilla: plain fetch() against /cart/change.js
  and /cart/update.js, with the one Shopify.onCartUpdate call already guarded
  by a typeof check, so it degrades cleanly on a theme that has no jQuery
  cart shim.

Pet Plus cart page - quantity steppers, remove, and note autosave.
 *
 * Progressive enhancement over templates/cart.liquid. Everything on that page
 * works without this file: the steppers wrap the `updates[]` input the theme
 * already posted, Remove is a real /cart/change link, and the Update cart
 * button submits the form. This script takes over only once it has confirmed
 * it can (see the `fetch` gate below), and marks that by putting `pp-js` on
 * the cart root - which is what hides the now-redundant Update button.
 *
 * How an update travels:
 *
 *   1. The stepper writes the new number into the input straight away, so the
 *      control never feels laggy.
 *   2. After a short debounce, POST /cart/change.js  { id: <line key>, ... }.
 *      Line KEY, not line index: indices shift the moment an earlier line is
 *      removed, and a queued update carrying a stale index would edit the
 *      wrong product.
 *   3. GET /cart?view=ajax and swap the two regions it returns. Re-rendering
 *      through Liquid rather than patching numbers in JavaScript is what
 *      keeps `money` formatting, line- and cart-level discounts and the
 *      free-delivery meter correct without any of that logic living here.
 *      See templates/cart.ajax.liquid.
 *
 * Requests are serialised - one in flight at a time, the rest held in
 * `pending` - because two overlapping edits to the same cart can come back in
 * either order, and the last response to land would win regardless of which
 * edit came last.
 */
(function () {
  'use strict';

  var root = document.querySelector('.pp-cart');
  var form = document.getElementById('cartform');
  var live = document.getElementById('pp-cart-live');
  var summary = document.getElementById('pp-cart-summary');

  /* No cart form on the page means the cart is empty - that template has no
     controls to enhance. */
  if (!root || !form || !live || !summary) return;

  /* One capability gate for the whole file. Anything without fetch also lacks
     Element.closest, DOMParser-on-text/html and classList, so failing here
     leaves the plain form path completely intact rather than half-upgraded. */
  if (!window.fetch || !window.DOMParser || !document.documentElement.closest) return;

  var QTY_DEBOUNCE = 320;
  var NOTE_DEBOUNCE = 800;

  /* Desired quantities keyed by line item key. A key is only ever present
     while its edit is waiting to be sent, so the newest value for a line
     always overwrites the one it has not sent yet. */
  var pending = Object.create(null);
  var timers = Object.create(null);
  var inflight = false;

  root.classList.add('pp-js');

  /* ---------------------------------------------------------------- *
   *  Helpers
   * ---------------------------------------------------------------- */

  /* Scanned rather than looked up with an attribute selector: a line item key
     is server-generated and can carry characters that would need escaping
     inside a selector string, and there are only ever a handful of lines. */
  function lineByKey(key) {
    var lines = live.querySelectorAll('.pp-line');
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].getAttribute('data-pp-key') === key) return lines[i];
    }
    return null;
  }

  function maxFor(input) {
    /* Only present when the variant tracks inventory and denies oversells, so
       an absent attribute genuinely means "no ceiling". */
    var max = parseInt(input.getAttribute('data-pp-max'), 10);
    return isNaN(max) ? Infinity : max;
  }

  function qtyOf(input) {
    var value = parseInt(input.value, 10);
    return isNaN(value) ? 1 : value;
  }

  /* Greys out minus at 1 and plus at the last unit in stock, so the stepper
     stops where the checkout would rather than bouncing off a 422. Re-run
     after every swap, since the buttons are new elements by then. */
  function syncSteppers() {
    var lines = live.querySelectorAll('.pp-line');
    for (var i = 0; i < lines.length; i++) {
      var input = lines[i].querySelector('.pp-qty-input');
      var minus = lines[i].querySelector('.pp-qty-btn[data-pp-step="-1"]');
      var plus = lines[i].querySelector('.pp-qty-btn[data-pp-step="1"]');
      if (!input) continue;
      var qty = qtyOf(input);
      if (minus) minus.disabled = qty <= 1;
      if (plus) plus.disabled = qty >= maxFor(input);
    }
  }

  function clearNotice() {
    var existing = live.querySelector('.pp-cart-error');
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
  }

  /* Shopify's own wording ("You can only add 3 of that item to your cart")
     is more useful than anything generic, so it is preferred when the
     endpoint supplies it. Inserted as a text node, never as markup. */
  function showNotice(message) {
    clearNotice();
    var notice = document.createElement('p');
    notice.className = 'pp-cart-error';
    notice.setAttribute('role', 'status');
    notice.innerHTML = '<i class="fa fa-exclamation-circle" aria-hidden="true"></i>';
    notice.appendChild(document.createTextNode(
      message || 'We could not update your cart just then. Please try again.'
    ));
    live.insertBefore(notice, live.firstChild);
  }

  /* ---------------------------------------------------------------- *
   *  Re-render
   * ---------------------------------------------------------------- */

  /* The meter arrives from the server already at its new width, so a CSS
     transition on a brand new element has nothing to travel from. Putting the
     old width back for one frame gives it a starting point - which is the
     whole point of the bar, since the movement is the feedback. */
  function replayMeter(previousWidth) {
    var fill = live.querySelector('.pp-ship-fill');
    if (!fill || previousWidth === null) return;

    var target = fill.style.width;
    if (target === previousWidth) return;

    fill.style.width = previousWidth;
    void fill.offsetWidth;
    window.requestAnimationFrame(function () {
      fill.style.width = target;
    });
  }

  function refresh() {
    var fill = live.querySelector('.pp-ship-fill');
    var previousWidth = fill ? fill.style.width : null;

    return fetch('/cart?view=ajax', {
      credentials: 'same-origin',
      headers: { 'Accept': 'text/html' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart view ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var nextLive = parsed.querySelector('[data-pp-region="live"]');
        var nextSummary = parsed.querySelector('[data-pp-region="summary"]');

        /* Either the alternate template is missing from this theme copy or
           something upstream rewrote the response. A reload still lands the
           shopper on a correct cart, so fall back to that rather than leaving
           stale numbers on screen. */
        if (!nextLive || !nextSummary) {
          window.location.reload();
          return;
        }

        var notice = live.querySelector('.pp-cart-error');
        live.innerHTML = nextLive.innerHTML;
        if (notice) live.insertBefore(notice, live.firstChild);
        summary.innerHTML = nextSummary.innerHTML;

        syncSteppers();
        replayMeter(previousWidth);
      });
  }

  /* ---------------------------------------------------------------- *
   *  Sending
   * ---------------------------------------------------------------- */

  function send(key, quantity) {
    inflight = true;

    var line = lineByKey(key);
    if (line) line.classList.add('is-busy');
    summary.classList.add('is-busy');

    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (response) {
        return response.json().then(function (body) {
          return { ok: response.ok, body: body };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          showNotice(result.body && (result.body.description || result.body.message));
          return refresh();
        }

        clearNotice();

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'queen-cart', cartData: result.body });
        }

        /* Keeps the header count and the cart drawer in step. Defined by
           snippets/api.jquery.custom.liquid, which theme.liquid includes on
           every page - guarded anyway, since a failure there must not take
           the cart page down with it. */
        if (window.Shopify && typeof window.Shopify.onCartUpdate === 'function') {
          try { window.Shopify.onCartUpdate(result.body); } catch (error) {}
        }

        /* The empty state lives outside the swapped regions, so the last
           removal is the one case that needs a real navigation. */
        if (result.body && result.body.item_count === 0) {
          window.location.reload();
          return;
        }

        return refresh();
      })
      .catch(function () {
        showNotice(null);
        /* Whatever the network did, the server is the authority on what is in
           the cart - so re-read it rather than trusting the optimistic value
           still sitting in the input. */
        return refresh().catch(function () {});
      })
      .then(function () {
        inflight = false;
        summary.classList.remove('is-busy');
        flush();
      });
  }

  function flush() {
    if (inflight) return;
    for (var key in pending) {
      var quantity = pending[key];
      delete pending[key];
      send(key, quantity);
      return;
    }
  }

  function queue(line, quantity, immediate) {
    var key = line.getAttribute('data-pp-key');
    if (!key) return;

    pending[key] = quantity;
    window.clearTimeout(timers[key]);

    if (immediate) {
      flush();
    } else {
      timers[key] = window.setTimeout(flush, QTY_DEBOUNCE);
    }
  }

  /* Writes the number to the input first and queues the round trip second, so
     the control responds on the click rather than on the response. */
  function setQuantity(line, quantity, immediate) {
    var input = line.querySelector('.pp-qty-input');
    if (!input) return;

    var max = maxFor(input);
    if (quantity > max) quantity = max;
    if (quantity < 0) quantity = 0;

    input.value = quantity;
    syncSteppers();
    queue(line, quantity, immediate);
  }

  /* ---------------------------------------------------------------- *
   *  Events
   *
   *  All delegated from the form, which survives every swap. Binding to the
   *  buttons themselves would work exactly once - the first re-render
   *  replaces them.
   * ---------------------------------------------------------------- */

  form.addEventListener('click', function (event) {
    var step = event.target.closest('.pp-qty-btn');
    if (step) {
      event.preventDefault();
      if (step.disabled) return;

      var stepLine = step.closest('.pp-line');
      var input = stepLine && stepLine.querySelector('.pp-qty-input');
      if (!input) return;

      /* Minus stops at 1. Emptying a line is what Remove is for, and a
         stepper that silently deletes a product on one extra click is a
         nasty surprise. */
      var next = qtyOf(input) + (parseInt(step.getAttribute('data-pp-step'), 10) || 0);
      if (next < 1) next = 1;

      setQuantity(stepLine, next, false);
      return;
    }

    var remove = event.target.closest('.pp-line-remove');
    if (remove) {
      event.preventDefault();
      var removeLine = remove.closest('.pp-line');
      if (removeLine) setQuantity(removeLine, 0, true);
    }
  });

  /* A typed quantity commits on blur. Anything that is not a whole number at
     least 1 falls back to 1 rather than emptying the line by accident. */
  form.addEventListener('change', function (event) {
    var input = event.target.closest && event.target.closest('.pp-qty-input');
    if (!input) return;

    var line = input.closest('.pp-line');
    if (!line) return;

    var typed = parseInt(input.value, 10);
    if (isNaN(typed) || typed < 1) typed = 1;

    setQuantity(line, typed, true);
  });

  form.addEventListener('keydown', function (event) {
    if (event.keyCode !== 13) return;

    var input = event.target.closest && event.target.closest('.pp-qty-input');
    if (!input) return;

    /* Enter in a quantity field would otherwise submit the form and reload
       the page, which is exactly the round trip this file exists to avoid. */
    event.preventDefault();
    input.blur();
  });

  /* ---------------------------------------------------------------- *
   *  Order note
   *
   *  The note used to be committed by the Update cart button, which is hidden
   *  once this file is running - so it saves itself instead. Without this the
   *  note would only reach Shopify if the shopper went on to check out, and a
   *  delivery instruction typed and then abandoned would be silently lost.
   * ---------------------------------------------------------------- */

  var note = form.querySelector('#note');
  var noteStatus = form.querySelector('[data-pp-note-status]');

  if (note) {
    var noteTimer = null;
    var noteHideTimer = null;

    var saveNote = function () {
      fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ note: note.value })
      })
        .then(function (response) {
          if (!response.ok || !noteStatus) return;

          noteStatus.textContent = 'Saved';
          noteStatus.classList.add('is-shown');
          window.clearTimeout(noteHideTimer);
          noteHideTimer = window.setTimeout(function () {
            noteStatus.classList.remove('is-shown');
          }, 2400);
        })
        /* A failed save is not worth interrupting anyone over: the note is
           still in the textarea and still rides along on the checkout post,
           which is exactly where it was before this ran. */
        .catch(function () {});
    };

    note.addEventListener('input', function () {
      window.clearTimeout(noteTimer);
      noteTimer = window.setTimeout(saveNote, NOTE_DEBOUNCE);
    });

    /* Catches the shopper who types and immediately clicks Checkout, before
       the debounce has had a chance to fire. */
    note.addEventListener('blur', function () {
      window.clearTimeout(noteTimer);
      saveNote();
    });
  }

  syncSteppers();
})();
